import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Mail, User, MapPin, Euro, ChevronLeft, 
  Copy, CheckCircle, Building, CreditCard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// RIB Information
const RIB_INFO = {
  titulaire: "FONDS DE DOTATION SECOND SOUFFLE PRODUCTION",
  iban: "FR98 1144 9000 0101 3932 9001 A44",
  bic: "BDEIFRPPXXX",
  banque: "FIDUCIAL BANQUE",
  adresse: "41 avenue Gambetta - 92400 Courbevoie"
};

export default function DonatePage() {
  const [step, setStep] = useState(1); // 1: formulaire, 2: RIB affiché
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(null);
  const [donorType, setDonorType] = useState('individual'); // individual or company
  const [formData, setFormData] = useState({
    amount: '',
    email: '',
    firstName: '',
    lastName: '',
    // Company fields
    companyName: '',
    siret: '',
    // Address
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    message: ''
  });
  const [donationId, setDonationId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Veuillez entrer un montant valide');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/donations`, {
        amount: parseFloat(formData.amount),
        donorType: donorType,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: donorType === 'company' ? formData.companyName : null,
        siret: donorType === 'company' ? formData.siret : null,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country,
        message: formData.message
      });

      if (response.data.success) {
        setDonationId(response.data.donation.id);
        setStep(2);
      }
    } catch (error) {
      console.error('Error submitting donation:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold text-[#D4AF37]">S2</span>
            <span className="text-white font-medium">Second Souffle</span>
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-[#a1a1aa] hover:text-white transition-colors"
          >
            <ChevronLeft size={18} />
            Retour au site
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          
          {step === 1 ? (
            <>
              {/* Header Section */}
              <div className="text-center mb-12">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
                  <Heart size={40} className="text-[#D4AF37]" />
                </div>
                <h1 className="text-3xl md:text-4xl font-serif text-white mb-4">
                  Faire un don
                </h1>
                <p className="text-[#a1a1aa] text-lg max-w-md mx-auto">
                  Soutenez le cinéma indépendant et donnez vie aux films de demain
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Donor Type Selection */}
                <div className="glass rounded-2xl p-6">
                  <h2 className="text-white font-medium mb-4 flex items-center gap-2">
                    <User size={18} className="text-[#D4AF37]" />
                    Vous êtes
                  </h2>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setDonorType('individual')}
                      className={`py-4 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-3 ${
                        donorType === 'individual'
                          ? 'bg-[#D4AF37] text-[#050505]'
                          : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      <User size={20} />
                      Particulier
                    </button>
                    <button
                      type="button"
                      onClick={() => setDonorType('company')}
                      className={`py-4 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-3 ${
                        donorType === 'company'
                          ? 'bg-[#D4AF37] text-[#050505]'
                          : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      <Building size={20} />
                      Entreprise
                    </button>
                  </div>
                </div>

                {/* Amount */}
                <div className="glass rounded-2xl p-6">
                  <h2 className="text-white font-medium mb-4 flex items-center gap-2">
                    <Euro size={18} className="text-[#D4AF37]" />
                    Montant du don
                  </h2>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      step="any"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      placeholder="Entrez le montant de votre don"
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-4 pr-12 text-white text-lg placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-medium text-lg">€</span>
                  </div>
                </div>

                {/* Personal/Company Info */}
                <div className="glass rounded-2xl p-6">
                  <h2 className="text-white font-medium mb-4 flex items-center gap-2">
                    {donorType === 'company' ? <Building size={18} className="text-[#D4AF37]" /> : <User size={18} className="text-[#D4AF37]" />}
                    {donorType === 'company' ? 'Informations entreprise' : 'Vos informations'}
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Company fields */}
                    {donorType === 'company' && (
                      <>
                        <div>
                          <label className="block text-[#a1a1aa] text-sm mb-2">Raison sociale *</label>
                          <div className="relative">
                            <Building size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                            <input
                              type="text"
                              required
                              value={formData.companyName}
                              onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                              className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                              placeholder="Nom de l'entreprise"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[#a1a1aa] text-sm mb-2">SIRET</label>
                          <input
                            type="text"
                            value={formData.siret}
                            onChange={(e) => setFormData({...formData, siret: e.target.value})}
                            className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                            placeholder="123 456 789 00012"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-[#a1a1aa] text-sm mb-2">Email *</label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                          placeholder="votre@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#a1a1aa] text-sm mb-2">
                          {donorType === 'company' ? 'Prénom du contact *' : 'Prénom *'}
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-[#a1a1aa] text-sm mb-2">
                          {donorType === 'company' ? 'Nom du contact *' : 'Nom *'}
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.lastName}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#a1a1aa] text-sm mb-2">Adresse *</label>
                      <input
                        type="text"
                        required
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                        placeholder="Numéro et nom de rue"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#a1a1aa] text-sm mb-2">Code postal *</label>
                        <input
                          type="text"
                          required
                          value={formData.postalCode}
                          onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-[#a1a1aa] text-sm mb-2">Ville *</label>
                        <input
                          type="text"
                          required
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#a1a1aa] text-sm mb-2">Message (optionnel)</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        rows={3}
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50 resize-none"
                        placeholder="Un mot pour l'équipe..."
                      />
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.amount}
                  className="w-full py-4 bg-[#D4AF37] text-[#050505] rounded-xl font-medium text-lg hover:bg-[#E6C762] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="donate-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <Heart size={20} />
                      Continuer vers le paiement
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Step 2: Show RIB */
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-400" />
              </div>
              
              <h1 className="text-3xl font-serif text-white mb-4">
                Merci pour votre générosité !
              </h1>
              <p className="text-[#a1a1aa] mb-8">
                Votre intention de don de <span className="text-[#D4AF37] font-medium">{formData.amount} €</span> a été enregistrée.
              </p>

              {/* RIB Card */}
              <div className="glass rounded-2xl p-8 text-left mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Building size={24} className="text-[#D4AF37]" />
                  <h2 className="text-xl font-medium text-white">Coordonnées bancaires</h2>
                </div>

                <p className="text-[#a1a1aa] text-sm mb-6">
                  Effectuez votre virement avec les informations suivantes.
                </p>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <p className="text-[#71717a] text-sm">Titulaire</p>
                      <p className="text-white">{RIB_INFO.titulaire}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <p className="text-[#71717a] text-sm">IBAN</p>
                      <p className="text-white font-mono">{RIB_INFO.iban}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(RIB_INFO.iban.replace(/\s/g, ''), 'iban')}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {copied === 'iban' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} className="text-[#71717a]" />}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <p className="text-[#71717a] text-sm">BIC</p>
                      <p className="text-white font-mono">{RIB_INFO.bic}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(RIB_INFO.bic, 'bic')}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {copied === 'bic' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} className="text-[#71717a]" />}
                    </button>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-[#71717a] text-sm">Banque</p>
                    <p className="text-white">{RIB_INFO.banque}</p>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="glass rounded-2xl p-6 text-left mb-8">
                <h3 className="text-white font-medium mb-3">Et maintenant ?</h3>
                <p className="text-[#a1a1aa] text-sm mb-4">
                  Nous reviendrons vers vous dès réception de votre virement.
                </p>
                <div className="space-y-3">
                  <a 
                    href="mailto:contact@secondsouffleprod.com"
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <Mail size={20} className="text-[#D4AF37]" />
                    <span className="text-white">contact@secondsouffleprod.com</span>
                  </a>
                  <a 
                    href="https://wa.me/33669066624"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="text-[#25D366]">WhatsApp : 06 69 06 66 24</span>
                  </a>
                </div>
              </div>

              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E6C762] transition-colors"
              >
                <ChevronLeft size={18} />
                Retour à l'accueil
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
